import { useState } from 'react';
import { Box, InputBase, Button, IconButton } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

/**
 * MessageInput component allows users to input and send messages.
 * It features a text input area where users can type their messages,
 * which supports multiline input with a maximum of 12 rows.
 *
 * Props:
 * - onSendMessage: function - Callback function triggered when a message is submitted.
 *
 * Behavior:
 * - Submits the message on pressing "Enter" without the "Shift" key, preventing new lines.
 * - Allows new lines when "Shift + Enter" is pressed.
 * - Clears the input field after a successful message submission.
 *
 * Styling:
 * - The form has a flex column layout with a border and background color.
 * - The input area is styled for flexible text input and auto-resizing.
 * - Includes an attach file icon and a send button within an action bar.
 */

const MessageInput = ({ onSendMessage }) => {
  const [message, setMessage] = useState('');

  const handleInputChange = e => {
    setMessage(e.target.value);
  };

  const handleSubmit = e => {
    e.preventDefault();
    if (message.trim()) {
      onSendMessage(message);
      setMessage('');
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{
        display: 'flex',
        gap: '10px',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '10px',
        border: '1px solid #5C5C5C',
        borderRadius: '16px',
        margin: '10px',
        backgroundColor: '#F9FAFE',
      }}
    >
      <InputBase
        sx={{
          flex: 1,
          padding: '5px 10px',
          borderRadius: '16px',
          width: '100%',
          overflow: 'auto',
          '& .MuiInputBase-input': {
            wordWrap: 'break-word',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            resize: 'none',
          },
        }}
        placeholder="Enter your message"
        inputProps={{
          'aria-label': 'enter your message',
          onKeyDown: e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault(); // Prevents new line
              handleSubmit(e);
            }
            // Shift + Enter will create a new line (default behavior)
          },
        }}
        value={message}
        onChange={handleInputChange}
        multiline
        maxRows={12}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <IconButton type="submit">
          <AttachFileIcon color="action" />
        </IconButton>
        <Button
          sx={{
            borderRadius: '12px',
          }}
          variant="contained"
          size="small"
          type="submit"
        >
          Send
        </Button>
      </Box>
    </Box>
  );
};

export default MessageInput;
